import {useEffect, useState, useRef} from 'react'
import {Tooltip} from 'bootstrap' // Import Bootstrap Tooltip
import {UserModel} from '../../auth'
import {getDailySales} from '../core/_requests'
import {IDailySalesDataBody} from '../core/_models'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {storeCurrencyLabel} from '../../common/currencyLabel'

type Props = {
  className: string
  language: string
  currentUser: UserModel | undefined
}

function getDateStart() {
  const date = new Date();
  const formatted_Date = date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const [monthValue, dayValue, yearValue] = formatted_Date.split('/');
  return `${yearValue}-${monthValue}-${dayValue}`;
}

function getDateEnd() {
  // Same as getDateStart, since you want the current date
  const date = new Date();
  const formatted_Date = date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const [monthValue, dayValue, yearValue] = formatted_Date.split('/');
  return `${yearValue}-${monthValue}-${dayValue}`;
}


// Define a type for the component's state, including the tooltip info
type DailySalesState = IDailySalesDataBody & {
  tooltipInfo: string
}

const DailySales: React.FC<Props> = ({language, currentUser, className}) => {
  const {formatMessage} = useIntl()
  const [state, setState] = useState<DailySalesState>({
    total_quantity: 0,
    total_sale_price: 0,
    tooltipInfo: '', // Initialize tooltipInfo
  })
  const [loading, setLoading] = useState<boolean>(true) // Add loading state
  const tooltipTriggerRef = useRef<HTMLElement | null>(null) // Ref for the tooltip icon

  useEffect(() => {
    const getData = async () => {
      setLoading(true) // Set loading true
      try {
        const {data} = await getDailySales(currentUser?.token, language)
        if (data && data.data) {
          setState({
            total_quantity: data.data.total_quantity,
            total_sale_price: data.data.total_sale_price,
            tooltipInfo: data.info || '',
          })
        } else {
           setState({ total_quantity: 0, total_sale_price: 0, tooltipInfo: '' });
        }
      } catch (error) {
         console.error('Error fetching daily sales data:', error)
         setState({ total_quantity: 0, total_sale_price: 0, tooltipInfo: '' });
      } finally {
         setLoading(false) // Set loading false
      }
    }
    getData()
  }, [currentUser?.token, language])

  // Effect for initializing tooltip after loading is complete
  useEffect(() => {
    if (!loading && tooltipTriggerRef.current) {
      const el = tooltipTriggerRef.current
      const existingTooltip = Tooltip.getInstance(el)
      if (existingTooltip) {
        existingTooltip.dispose() // Remove old instance
      }
      // Initialize new tooltip on the icon
      new Tooltip(el, {
        html: true,
        customClass: 'tooltip-light', // Use light theme
      })
    }
    // Cleanup function
    return () => {
      if (tooltipTriggerRef.current) {
        const existingTooltip = Tooltip.getInstance(tooltipTriggerRef.current)
        if (existingTooltip) {
          existingTooltip.dispose()
        }
      }
    }
  }, [loading, state.tooltipInfo]) // Re-run if loading changes or info text changes


  return (
    <div className={`hsb-card hsb-card--dark ${className}`}>
      <div>
        <div className='hsb-card-head'>
          <span className='hsb-chip hsb-chip--surface'>
            <span className='material-symbols-outlined'>trending_up</span>
          </span>
        </div>
        <span className='hsb-kpi-label'>
          {formatMessage({ id: "daily_sales" })}
          <span className='fw-semibold' style={{padding: '0 4px'}}>{state.total_quantity}</span>
          {formatMessage({ id: "daily_sold_prod_quantity" })}
          <i
            ref={tooltipTriggerRef} // Attach ref here
            className='fas fa-exclamation-circle hsb-info-icon'
            data-bs-toggle='tooltip' // Keep tooltip attributes
            data-bs-placement='top'
            data-bs-custom-class='tooltip-light' // Use light theme
            data-bs-html='true'
            title={state.tooltipInfo || ''} // Set title from state
          ></i>
        </span>
        <h2 className='hsb-kpi-value'>
          {state.total_sale_price} <span className='hsb-kpi-unit'>{storeCurrencyLabel(formatMessage)}</span>
        </h2>
      </div>
      <Link
        to={`/sales/sales-report?filter_date_start=${getDateStart()}&filter_date_end=${getDateEnd()}`}
        className='hsb-link'
      >
        {formatMessage({ id: "sales_report" })}
        <span className='material-symbols-outlined'>analytics</span>
      </Link>
    </div>
  )
}

export default DailySales
